import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  CubeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface SizeRow {
  variant_id: number;
  sku: string;
  color: string;
  color_code?: string | null;
  size: string;
  stock: number;
  reserved: number;
  available: number;
  is_active: boolean;
}

interface VariantGroup {
  variant_id: number;
  sku: string;
  color: string;
  color_code?: string | null;
  is_active: boolean;
  total_stock: number;
  total_reserved: number;
  total_available: number;
  sizes: SizeRow[];
}

interface DetailResponse {
  product: {
    id: number;
    name: string;
    product_code: string;
    barcode?: string | null;
    price: number;
    cost_price: number;
    total_stock: number;
    low_stock_threshold?: number;
    stock_status?: 'out' | 'low' | 'in_stock';
    category?: string | null;
    brand?: string | null;
  };
  stock_details: SizeRow[];
  variants: VariantGroup[];
  movements: any[];
  purchases: any[];
  sales: any[];
}

type TabKey = 'stock' | 'movements' | 'purchases' | 'sales';

const stockBadgeClass = (qty: number) =>
  qty <= 0
    ? 'bg-red-100 text-red-800'
    : qty <= 10
    ? 'bg-yellow-100 text-yellow-800'
    : 'bg-green-100 text-green-800';

const InventoryProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('stock');
  const [movementTypeFilter, setMovementTypeFilter] = useState<string>('');

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiService.get(`/pos/inventory/product/${id}`);
      setData(response);
    } catch (error) {
      console.error('Failed to load inventory detail:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  if (!data) {
    return (
      <div className="p-8 text-center text-gray-500">
        Product not found.
        <div className="mt-4">
          <button
            onClick={() => navigate('/admin/inventory')}
            className="text-indigo-600 hover:text-indigo-800"
          >
            Back to Inventory
          </button>
        </div>
      </div>
    );
  }

  const { product, variants, movements, purchases, sales } = data;
  const totalReserved = variants.reduce((sum, v) => sum + v.total_reserved, 0);
  const totalAvailable = variants.reduce((sum, v) => sum + v.total_available, 0);
  const threshold = product.low_stock_threshold ?? 10;
  const isLow = product.stock_status === 'low';
  const isOut = product.stock_status === 'out';

  // Distinct movement types for the filter dropdown.
  const movementTypes = Array.from(
    new Set(movements.map((m: any) => String(m.type)))
  ).sort();
  const filteredMovements = movementTypeFilter
    ? movements.filter((m: any) => String(m.type) === movementTypeFilter)
    : movements;

  const exportStockCsv = () => {
    const header = ['Color', 'SKU', 'Size', 'On Hand', 'Reserved', 'Available', 'Status'];
    const rows: string[][] = [];
    variants.forEach((v) => {
      v.sizes.forEach((s) => {
        rows.push([
          v.color,
          v.sku,
          s.size,
          String(s.stock),
          String(s.reserved),
          String(s.available),
          s.is_active ? 'Active' : 'Inactive',
        ]);
      });
    });

    const escape = (val: string) => `"${String(val).replace(/"/g, '""')}"`;
    const csv = [header, ...rows]
      .map((r) => r.map(escape).join(','))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory_${product.product_code}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'stock', label: 'Stock by Variant' },
    { key: 'movements', label: 'Movements', count: movements.length },
    { key: 'purchases', label: 'Purchases', count: purchases.length },
    { key: 'sales', label: 'POS Sales', count: sales.length },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/inventory')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Inventory
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{product.name}</h1>
            <p className="text-gray-600 font-mono text-sm">{product.product_code}</p>
            <p className="text-sm text-gray-500 mt-1">
              {product.category || '-'}
              {product.brand ? ` • ${product.brand}` : ''}
            </p>
          </div>
          <div className="flex items-start gap-4">
            <div className="text-right">
              <p className="text-sm text-gray-500">Sell Price</p>
              <p className="text-xl font-bold text-gray-900">
                {Number(product.price).toLocaleString()} MMK
              </p>
            </div>
            <button
              onClick={exportStockCsv}
              className="mt-1 inline-flex items-center gap-1.5 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Low / out of stock alert */}
      {(isLow || isOut) && (
        <div
          className={`flex items-center gap-2 rounded-lg border px-4 py-3 text-sm ${
            isOut
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-yellow-200 bg-yellow-50 text-yellow-800'
          }`}
        >
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          {isOut ? (
            <span>This product is out of stock.</span>
          ) : (
            <span>
              Low stock: {product.total_stock} left, at or below the threshold of {threshold}.
            </span>
          )}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Total Stock</p>
          <p className="text-2xl font-bold text-gray-900">{product.total_stock}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Reserved</p>
          <p className="text-2xl font-bold text-yellow-600">{totalReserved}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Available</p>
          <p className="text-2xl font-bold text-green-600">{totalAvailable}</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Variants</p>
          <p className="text-2xl font-bold text-gray-900">{variants.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 px-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {typeof tab.count === 'number' && (
                  <span className="ml-1.5 text-xs text-gray-400">({tab.count})</span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Stock by Variant */}
        {activeTab === 'stock' && (
          <div className="p-6 space-y-6">
            {variants.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <CubeIcon className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                This product has no variants.
              </div>
            ) : (
              variants.map((variant) => (
                <div
                  key={variant.variant_id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="flex items-center justify-between bg-gray-50 px-4 py-3">
                    <div className="flex items-center gap-3">
                      {variant.color_code && (
                        <span
                          className="inline-block h-5 w-5 rounded-full border border-gray-300"
                          style={{ backgroundColor: variant.color_code }}
                          title={variant.color}
                        />
                      )}
                      <span className="font-medium text-gray-900">{variant.color}</span>
                      <span className="text-xs text-gray-400 font-mono">{variant.sku}</span>
                      {!variant.is_active && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      Stock <strong className="text-gray-900">{variant.total_stock}</strong>
                      <span className="mx-2 text-gray-300">|</span>
                      Reserved{' '}
                      <strong className="text-yellow-600">{variant.total_reserved}</strong>
                      <span className="mx-2 text-gray-300">|</span>
                      Available{' '}
                      <strong className="text-green-600">{variant.total_available}</strong>
                    </div>
                  </div>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Size
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                          On Hand
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                          Reserved
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                          Available
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {variant.sizes.map((s) => (
                        <tr key={`${s.variant_id}-${s.size}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-sm font-medium text-gray-900">
                            {s.size || '-'}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockBadgeClass(
                                s.available
                              )}`}
                            >
                              {s.available <= 0 && (
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                              )}
                              {s.stock}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-center text-sm text-yellow-600">
                            {s.reserved}
                          </td>
                          <td className="px-4 py-2.5 text-center text-sm font-medium text-green-600">
                            {s.available}
                          </td>
                          <td className="px-4 py-2.5 text-center">
                            {s.is_active ? (
                              <span className="text-xs text-gray-500">Active</span>
                            ) : (
                              <span className="text-xs text-gray-400">Inactive</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </div>
        )}

        {/* Movements */}
        {activeTab === 'movements' && (
          <div>
            {movementTypes.length > 0 && (
              <div className="p-4 border-b border-gray-200 flex items-center gap-3">
                <label className="text-sm text-gray-500">Type</label>
                <select
                  value={movementTypeFilter}
                  onChange={(e) => setMovementTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All types</option>
                  {movementTypes.map((t) => (
                    <option key={t} value={t}>
                      {t.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-400">
                  {filteredMovements.length} of {movements.length}
                </span>
              </div>
            )}
            <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variant / Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Before</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">After</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredMovements.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                      No stock movements recorded.
                    </td>
                  </tr>
                ) : (
                  filteredMovements.map((m: any) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(m.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {m.variant?.color ? `${m.variant.color}` : '-'}
                        {m.size_name ? ` / ${m.size_name}` : ''}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            m.quantity > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {String(m.type).replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm font-bold">
                        <span className={m.quantity > 0 ? 'text-green-600' : 'text-red-600'}>
                          {m.quantity > 0 ? '+' : ''}
                          {m.quantity}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-500">{m.quantity_before}</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-900 font-medium">{m.quantity_after}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-[200px] truncate">{m.reason || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{m.created_by?.name || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Purchases */}
        {activeTab === 'purchases' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {purchases.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      No purchase history.
                    </td>
                  </tr>
                ) : (
                  purchases.map((p: any) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(p.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {p.purchase?.supplier?.name || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{p.size_name || '-'}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-green-600">
                        +{p.quantity}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">
                        {Number(p.cost_price).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900 font-medium">
                        {Number(p.total).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* POS Sales */}
        {activeTab === 'sales' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sales.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      No POS sales history.
                    </td>
                  </tr>
                ) : (
                  sales.map((s: any) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{s.size_name || '-'}</td>
                      <td className="px-6 py-4 text-center text-sm font-medium text-red-600">
                        -{s.quantity}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">
                        {Number(s.unit_price).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-900 font-medium">
                        {Number(s.total).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryProductDetail;
